import React from 'react';
import { Link } from 'react-router-dom';
import { usePermissions, Permissions } from '../contexts/PermissionsContext';

// Traffic Light Health Data (Phase 2A)
// HealthScoreData interface converted to JSDoc comments for reference:
// {
//   status: 'GREEN' | 'YELLOW' | 'RED';
//   status_emoji: string;
//   status_color: string;
//   status_message: string;
//   warnings_count?: number;
//   high_priority_count?: number;
//   // Backward compatibility
//   health_score?: number;
//   grade?: string;
//   grade_label?: string;
//   grade_color?: string;
// }

// ReportCardProps interface converted to JSDoc comments for reference:
// {
//   report: {
//     id: string;
//     client_name: string;
//     claimant_name: string;
//     status: string;
//     created_at: string;
//     pdf_url?: string;
//     result?: {
//       final_claim_amount?: number;
//     };
//   };
//   healthScore?: HealthScoreData | null;
//   collaborationStatus?: {
//     invite_status: string;
//     invite_email?: string;
//     status?: string;
//   } | null;
//   onDuplicate?: (reportId: string) => void;
//   onDelete?: (reportId: string) => void;
//   onShare?: (reportId: string) => void;
//   onDownload?: (reportId: string) => void;
//   isSelected?: boolean;
//   onSelect?: (reportId: string, checked: boolean) => void;
//   bulkMode?: boolean;
// }

// Traffic light badge component
const TrafficLightBadge = ({ healthData }) => {
  // New traffic light system
  if (healthData.status && ['GREEN', 'YELLOW', 'RED'].includes(healthData.status)) {
    const config = {
      GREEN: { 
        emoji: '🟢', 
        bg: 'bg-green-100', 
        text: 'text-green-700',
        label: 'Ready'
      },
      YELLOW: { 
        emoji: '🟡', 
        bg: 'bg-yellow-100', 
        text: 'text-yellow-700',
        label: 'Review'
      },
      RED: { 
        emoji: '🔴', 
        bg: 'bg-red-100', 
        text: 'text-red-700',
        label: 'Issues Found'
      }
    }[healthData.status];

    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${config.bg}`}>
        <span>{config.emoji}</span>
        <span className={`font-bold ${config.text}`}>{config.label}</span>
        {healthData.high_priority_count && healthData.high_priority_count > 0 && (
          <span className="bg-red-200 text-red-800 px-1.5 py-0.5 rounded-full text-[10px] font-bold">
            {healthData.high_priority_count}
          </span>
        )}
      </div>
    );
  }

  // Backward compatibility: Old 0-100 score system
  if (typeof healthData.health_score === 'number') {
    const score = healthData.health_score;
    const config = score >= 80 
      ? { emoji: '🟢', bg: 'bg-green-100', text: 'text-green-600' }
      : score >= 60 
        ? { emoji: '🟡', bg: 'bg-yellow-100', text: 'text-yellow-600' }
        : { emoji: '🔴', bg: 'bg-red-100', text: 'text-red-600' };

    return (
      <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${config.bg}`}>
        <span>{config.emoji}</span>
        <span className={`font-bold ${config.text}`}>
          {Math.round(score)}/100
        </span>
      </div>
    );
  }

  return null;
};

// Collaboration Status Badge Component
const CollaborationBadge = ({ collaborationStatus }) => {
  if (!collaborationStatus) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-gray-100">
        <span>📤</span>
        <span className="font-medium text-gray-700">Not Shared</span>
      </div>
    );
  }

  const configs = {
    pending: {
      emoji: '📧',
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      label: 'Invited'
    },
    accepted: {
      emoji: '✅',
      bg: 'bg-green-100',
      text: 'text-green-700',
      label: 'Accepted'
    },
    in_review: {
      emoji: '📝',
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      label: 'In Review'
    },
    complete: {
      emoji: '✓',
      bg: 'bg-teal-100',
      text: 'text-teal-700',
      label: 'Complete'
    },
    needs_update: {
      emoji: '🔄',
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      label: 'Needs Update'
    }
  };

  // Use invite_status (pending/accepted) not status (active/revoked)
  const status = collaborationStatus.invite_status || 'pending';
  const config = configs[status] || configs.pending;

  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs ${config.bg}`} title={collaborationStatus.invite_email}>
      <span>{config.emoji}</span>
      <span className={`font-medium ${config.text}`}>{config.label}</span>
    </div>
  );
};

export default function ReportCard({ report, healthScore, collaborationStatus, onDuplicate, onDelete, onShare, onDownload, selected, onSelect, bulkMode }) {
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(Permissions.CLAIMS_DELETE);
  const canCreate = hasPermission(Permissions.CLAIMS_CREATE); // For duplicate
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDuplicate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDuplicate) {
      onDuplicate(report.id);
    }
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onShare) {
      onShare(report.id);
    }
  };

  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDownload && report.pdf_url) {
      window.open(report.pdf_url, '_blank');
    }
  };

  return (
    <div className={`border rounded-lg hover:shadow-lg transition-shadow ${
      selected ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-200'
    }`}>
      {/* Bulk Selection Checkbox */}
      {bulkMode && (
        <div className="px-4 pt-4 pb-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={selected || false}
              onChange={(e) => onSelect && onSelect(report.id, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Select</span>
          </label>
        </div>
      )}

      <Link to={`/report/${report.id}`} className="block p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg text-gray-900 hover:text-blue-600 transition-colors">
              {report.client_name}
            </h3>
            <p className="text-sm text-gray-600">Claimant: {report.claimant_name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Generated: {new Date(report.created_at).toLocaleDateString()}
            </p>
            <p className="text-xs text-gray-400 mt-1 font-mono" title="Click to copy">
              ID: {report.id.substring(0, 8)}...
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigator.clipboard.writeText(report.id);
                }}
                className="ml-2 text-blue-500 hover:text-blue-700"
              >
                📋
              </button>
            </p>
          </div>
          <div className="text-right ml-4">
            <p className="text-2xl font-bold text-blue-600">
              {report.result?.final_claim_amount
                ? `$${Number(report.result.final_claim_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : report.status === 'draft' 
                  ? 'Draft'
                  : report.status === 'processing'
                  ? 'Processing'
                  : report.status === 'completed'
                  ? 'Completed'
                  : report.status === 'causation_failed'
                  ? 'Review Needed'
                  : report.status}
            </p>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-2 ${
              report.status === 'causation_failed' 
                ? 'bg-amber-100 text-amber-800' 
                : getStatusColor(report.status)
            }`}>
              {report.status === 'draft' ? 'Draft' : 
               report.status === 'processing' ? 'Processing' : 
               report.status === 'completed' ? 'Completed' : 
               report.status === 'causation_failed' ? 'Causation Test Failed' :
               report.status}
            </span>
            {/* Health Score Badge - Traffic Light System */}
            {healthScore && report.status === 'completed' && (
              <div className="mt-2">
                <TrafficLightBadge healthData={healthScore} />
              </div>
            )}
            {/* Collaboration Status Badge */}
            {report.status === 'completed' && (
              <div className="mt-2">
                <CollaborationBadge collaborationStatus={collaborationStatus} />
              </div>
            )}
          </div>
        </div>
      </Link>
      
      {/* Quick Action Buttons */}
      <div className="border-t pt-3 px-4 pb-4">
        {report.status === 'completed' ? (
          <div className="flex flex-wrap gap-2">
            {/* View Button */}
            <Link
              to={`/report/${report.id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-[100px] text-center text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-3 rounded-lg transition inline-flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              View
            </Link>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="flex-1 min-w-[100px] text-sm bg-green-100 hover:bg-green-200 text-green-700 font-semibold py-2 px-3 rounded-lg transition inline-flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Share
            </button>

            {/* Download PDF Button */}
            {report.pdf_url && (
              <button
                onClick={handleDownload}
                className="flex-1 min-w-[100px] text-sm bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold py-2 px-3 rounded-lg transition inline-flex items-center justify-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                PDF
              </button>
            )}

            {/* Duplicate Button */}
            <button
              onClick={handleDuplicate}
              className="flex-1 min-w-[100px] text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold py-2 px-3 rounded-lg transition inline-flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Duplicate
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            {/* Draft/Processing Reports - Resume/Edit Button */}
            <Link
              to={`/wizard-v2?resume=${report.id}`}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 text-sm bg-orange-100 hover:bg-orange-200 text-orange-700 font-semibold py-2 px-4 rounded-lg transition inline-flex items-center justify-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {report.status === 'draft' ? 'Resume & Complete' : 'View & Edit'}
            </Link>
            
            {/* Delete Button - Only show if user has delete permission */}
            {canDelete && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (window.confirm(`Are you sure you want to delete "${report.client_name}"?\n\nThis action cannot be undone.`)) {
                    console.log('Delete report:', report.id);
                    if (onDelete) {
                      onDelete(report.id);
                    }
                  }
                }}
                className="text-sm bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-2 px-4 rounded-lg transition inline-flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
